import React from 'react';
import { CollapsibleSection } from '../common/CollapsibleSection';
import { ResultsHistogram } from '../charts/HistogramChart';
import { DonutChart } from '../charts/DonutChart';
import { TrialResult } from '../../types';

interface ResultsDisplayProps {
  results: TrialResult[];
  isVisible: boolean;
  className?: string;
}

/**
 * Component for displaying final simulation results and analysis
 */
export const ResultsDisplay: React.FC<ResultsDisplayProps> = ({
  results,
  isVisible,
  className = ''
}) => {
  if (!isVisible || results.length === 0) {
    return null;
  }

  // Calculate summary statistics
  const calculateStats = () => {
    const successfulTrials = results.filter(r => r.finalBalance > 0 && !r.wasOverdrawn).length;
    const totalTrials = results.length;
    const successRate = totalTrials > 0 ? (successfulTrials / totalTrials) * 100 : 0;
    
    const finalBalances = results.map(r => r.finalBalance);
    const avgBalance = finalBalances.reduce((sum, balance) => sum + balance, 0) / finalBalances.length;
    const medianBalance = [...finalBalances].sort((a, b) => a - b)[Math.floor(finalBalances.length / 2)];
    
    const negativeBalanceTrials = results.filter(r => r.finalBalance <= 0).length;
    const negativeBalanceRate = totalTrials > 0 ? (negativeBalanceTrials / totalTrials) * 100 : 0;
    
    // Overdrawn trials: final balance is positive but was overdrawn at some point
    const overdrawnTrials = results.filter(r => r.finalBalance > 0 && r.wasOverdrawn).length;
    const overdrawnRate = totalTrials > 0 ? (overdrawnTrials / totalTrials) * 100 : 0;
    
    return {
      successRate,
      negativeBalanceRate,
      overdrawnRate,
      avgBalance,
      medianBalance,
      totalTrials,
      successfulTrials,
      negativeBalanceTrials,
      overdrawnTrials
    };
  };

  const stats = calculateStats();

  const formatCurrency = (value: number): string => {
    return value.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const formatPercent = (value: number): string => {
    return `${value.toFixed(1)}%`;
  };

  return (
    <CollapsibleSection
      title="Results"
      defaultCollapsed={false}
      className={`results-display ${className}`}
    >
      <div className="results-content">
        {/* Trial Outcomes Donut Chart */}
        <div className="outcomes-chart-section">
          <DonutChart
            successfulTrials={stats.successfulTrials}
            negativeBalanceTrials={stats.negativeBalanceTrials}
            overdrawnTrials={stats.overdrawnTrials}
            className="outcomes-donut-chart"
          />
        </div>

        {/* Summary Statistics */}
        <div className="results-summary">
          <div className="stats-grid">
            <div className="stat-item success-rate">
              <div className="stat-label">Success Rate</div>
              <div className="stat-value success">
                {formatPercent(stats.successRate)}
              </div>
              <div className="stat-description">
                Ended positive, never overdrawn ({stats.successfulTrials} of {stats.totalTrials} trials)
              </div>
            </div>

            <div className="stat-item negative-balance-rate">
              <div className="stat-label">Negative Balance Rate</div>
              <div className="stat-value error">
                {formatPercent(stats.negativeBalanceRate)}
              </div>
              <div className="stat-description">
                Ended with negative balance ({stats.negativeBalanceTrials} trials)
              </div>
            </div>

            <div className="stat-item overdrawn-rate">
              <div className="stat-label">Overdrawn Rate</div>
              <div className="stat-value warning">
                {formatPercent(stats.overdrawnRate)}
              </div>
              <div className="stat-description">
                Ended up positive but overdrawn at some point ({stats.overdrawnTrials} trials)
              </div>
            </div>

            <div className="stat-item avg-balance">
              <div className="stat-label">Average Final Balance</div>
              <div className="stat-value">
                {formatCurrency(stats.avgBalance)}
              </div>
              <div className="stat-description">
                Mean across all trials
              </div>
            </div>

            <div className="stat-item median-balance">
              <div className="stat-label">Median Final Balance</div>
              <div className="stat-value">
                {formatCurrency(stats.medianBalance)}
              </div>
              <div className="stat-description">
                50th percentile result
              </div>
            </div>

            <div className="stat-item total-trials">
              <div className="stat-label">Total Trials</div>
              <div className="stat-value">
                {stats.totalTrials.toLocaleString()}
              </div>
              <div className="stat-description">
                Monte Carlo simulations run
              </div>
            </div>
          </div>
        </div>

        {/* Results Histogram */}
        <div className="results-chart">
          <h3>Final Balance Distribution</h3>
          <ResultsHistogram
            results={results.map(r => ({ finalBalance: r.finalBalance, wasOverdrawn: r.wasOverdrawn }))}
            className="final-results-histogram"
          />
        </div>

        {/* Interpretation Guide */}
        <div className="results-interpretation">
          <h3>How to Interpret These Results</h3>
          
          <div className="interpretation-content">
            <div className="interpretation-item">
              <h4>Success Rate</h4>
              <p>
                The percentage of simulations where you never ran out of money and ended with a positive balance.
                A higher success rate indicates a more robust financial plan.
              </p>
            </div>

            <div className="interpretation-item">
              <h4>Negative Balance Rate</h4>
              <p>
                The percentage of simulations where you ended with a negative balance.
                A lower negative balance rate indicates a more robust financial plan.
              </p>
            </div>

            <div className="interpretation-item">
              <h4>Overdrawn Rate</h4>
              <p>
                The percentage of simulations where you temporarily went negative but recovered to end with a positive balance.
                These scenarios indicate periods of financial stress but ultimate recovery.
              </p>
            </div>

            <div className="interpretation-item">
              <h4>Distribution Shape</h4>
              <p>
                The histogram shows the range of possible outcomes. A wider distribution indicates more uncertainty.
                Red bars represent scenarios where you ended with zero or negative balance.
              </p>
            </div>

            <div className="interpretation-recommendations">
              <h4>Recommendations</h4>
              <ul>
                <li><strong>Success rate &gt; 80%:</strong> Your plan looks robust</li>
                <li><strong>Success rate 60-80%:</strong> Consider increasing savings or reducing retirement spending</li>
                <li><strong>Success rate &lt; 60%:</strong> Your plan may need significant adjustments</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </CollapsibleSection>
  );
};
